// CUSTOM PRICE — the negotiated fee for one patient, one specialty and one
// appointment type.
//
// WHOSE PRICES (owner, 16 Sep 2026). A doctor prices their own work, so the
// doctor is simply their own login. An ADMIN sets them on behalf of a doctor
// and therefore has to say which doctor first: the picker at the top of the
// tab. Until they pick, the tab lists every doctor's rows read-only, exactly
// as it did before; once picked, the list narrows to that doctor
// (/custom-price/patient/:id/doctor/:doctorId) and the same add / edit /
// delete form a doctor uses is enabled against them. The form is built from
// the chosen doctor's `appointmentSpeciality`, which is why a doctor record
// has to be loaded before anything can be priced.
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
    createCustomPrice,
    updateCustomPrice,
    deleteCustomPrice,
    getCustomPricesByPatientId,
    getCustomPricesByPatientAndDoctor
} from '../../redux/customPriceSlice';
import { getDoctorNameList, getDoctorById } from '../../redux/doctorSlice';
import emptykey from '../../assets/img/colorkit.svg';
import { Plus, X, Trash2, Edit, ChevronLeft, ChevronRight, ChevronDown, Search, Stethoscope, User, DollarSign, TrendingUp, TrendingDown, FileText } from 'lucide-react';
import { useMasterLabel } from '../../hooks/useMasterLabel';
import { doctorCurrencySymbol } from '../../utils/currency';

// Per-row translated <option> for the specialty picker in CustomPrice.
// The <select> stores the specialtyId as its value, so we only translate the label.
const SpecialtyOption = ({ spec }) => {
    const label = useMasterLabel('masterSpecialty', spec.specialtyId, 'name', spec.name);
    return <option value={spec.specialtyId}>{label}</option>;
};

// Custom-price card heading. Resolves the specialty via its ID so language switches
// update the card without refetching. Accepts the snapshot as fallback.
const PriceCardSpecialtyName = ({ specialityId, fallback }) => {
    const label = useMasterLabel('masterSpecialty', specialityId, 'name', fallback);
    return <>{label}</>;
};

const CustomPrice = () => {
    const { t, i18n } = useTranslation(['customPrice']);
    // 1 Oct 2026: the server's refusals carry { en, fr } — custom_pricing is
    // a plan control now (403 PLAN_FEATURE_MISSING when the doctor's plan
    // lacks it), and that sentence is shown in the reader's language.
    const lang = (i18n?.language || 'en').slice(0, 2);
    const location = useLocation();
    const dispatch = useDispatch();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const { selectedPatient } = useSelector((state) => state.patient);
    const { selectedDoctor, doctorNameList, loadingList } = useSelector((state) => state.doctor);
    const { customPrices, pagination, loading, error } = useSelector((state) => state.customPrice);

    const userRole = localStorage.getItem('Role');
    const isAdmin = userRole === 'admin';
    const patientId = userRole === 'admin' || userRole === 'doctor'
        ? (location?.state?.patientId || selectedPatient?.patientId)
        : localStorage.getItem('ID');

    // The admin's doctor picker.
    const [pickedDoctor, setPickedDoctor] = useState(null);
    const [doctorPickerOpen, setDoctorPickerOpen] = useState(false);
    const [doctorSearch, setDoctorSearch] = useState('');
    const doctorPickerRef = useRef(null);

    // The doctor these prices belong to: a doctor's own login, or the one an
    // admin picked. Everything that writes is keyed off this.
    const doctorId = userRole === 'doctor'
        ? localStorage.getItem('ID')
        : (isAdmin ? (pickedDoctor?.doctorId || null) : null);
    const canEdit = !!doctorId;

    // The specialty / appointment-type tree the form prices against. For an
    // admin it is only usable once the loaded doctor IS the picked one — a
    // doctor left in the store by another screen must never seed this form.
    const activeDoctor = userRole === 'doctor'
        ? selectedDoctor
        : (pickedDoctor && selectedDoctor?.doctorId === pickedDoctor.doctorId ? selectedDoctor : null);
    const appointmentSpeciality = activeDoctor?.appointmentSpeciality || [];

    const [formData, setFormData] = useState({
        specialtyId: '',
        appointmentTypeId: '',
        actualPrice: '',
        customPrice: '',
    });

    const [priceError, setPriceError] = useState('');

    // Fetch custom prices when page changes. With a doctor in hand (a doctor's
    // own login, or the one the admin picked) the list narrows to that doctor;
    // without one an admin sees every doctor's rows.
    useEffect(() => {
        if (patientId) {
            if (doctorId) {
                dispatch(getCustomPricesByPatientAndDoctor({
                    patientId,
                    doctorId,
                    page: currentPage,
                    limit: 5
                }));
            } else {
                dispatch(getCustomPricesByPatientId({
                    patientId,
                    page: currentPage,
                    limit: 5
                }));
            }
        }
    }, [dispatch, patientId, doctorId, currentPage, userRole]);

    const getSpecialtyName = (id) => {
        const spec = appointmentSpeciality.find((s) => s.specialtyId === id);
        return spec ? spec.name : t('customPrice.unknown');
    };

    const getAppointmentTypeName = (id, specialtyId) => {
        const spec = appointmentSpeciality.find((s) => s.specialtyId === specialtyId);
        if (!spec) return t('customPrice.unknown');
        const appt = spec.appointmentTypes?.find((a) => a.typeId === id);
        return appt ? appt.type : t('customPrice.unknown');
    };

    // The doctor's currency, from their country's currency code (utils/currency.js).
    const getCurrencySymbol = () => doctorCurrencySymbol(activeDoctor);

    const formatPrice = (price) => {
        const numericPrice = typeof price === 'number' ? price : parseFloat(price);
        const currencySymbol = getCurrencySymbol();
        return isNaN(numericPrice) ? t('customPrice.na') : `${currencySymbol} ${numericPrice.toFixed(2)}`;
    };

    useEffect(() => {
        if (formData.appointmentTypeId && !isEditing) {
            const selectedSpecialityObj = appointmentSpeciality.find(
                (item) => item.specialtyId === formData.specialtyId
            );
            const selectedAppt = selectedSpecialityObj?.appointmentTypes?.find(
                (appt) => appt.typeId === formData.appointmentTypeId
            );
            if (selectedAppt && selectedAppt.price) {
                setFormData((prev) => ({
                    ...prev,
                    actualPrice: selectedAppt.price.toString(),
                    customPrice: selectedAppt.price.toString(),
                }));
            }
        }
    }, [formData.appointmentTypeId, appointmentSpeciality, isEditing]);

    // The admin's doctor list — the same endpoint every other admin screen
    // lists doctors from. Debounced while typing so a search is one request.
    useEffect(() => {
        if (!isAdmin) return undefined;
        const query = doctorSearch.trim();
        const handle = setTimeout(() => {
            dispatch(getDoctorNameList(query));
        }, query ? 300 : 0);
        return () => clearTimeout(handle);
    }, [dispatch, isAdmin, doctorSearch]);

    // Close the picker on an outside click, like the other doctor pickers.
    useEffect(() => {
        if (!doctorPickerOpen) return undefined;
        const onMouseDown = (e) => {
            if (doctorPickerRef.current && !doctorPickerRef.current.contains(e.target)) {
                setDoctorPickerOpen(false);
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [doctorPickerOpen]);

    const handleAddClick = () => {
        setIsEditing(false);
        setEditingItem(null);
        setIsModalOpen(true);
        setPriceError('');
        setFormData({
            specialtyId: '',
            appointmentTypeId: '',
            actualPrice: '',
            customPrice: '',
        });
    };

    const handleEditClick = (item) => {
        setIsEditing(true);
        setEditingItem(item);
        setFormData({
            specialtyId: item.specialityId,
            appointmentTypeId: item.appointmentTypeId,
            actualPrice: item.actualPrice ? item.actualPrice.toString() : '',
            customPrice: item.customPrice.toString(),
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setIsEditing(false);
        setEditingItem(null);
        setPriceError('');
        setFormData({
            specialtyId: '',
            appointmentTypeId: '',
            actualPrice: '',
            customPrice: '',
        });
    };

    // Picking a doctor re-reads BOTH halves: the rows (filtered to that doctor)
    // and the doctor record, which carries the specialties the form prices
    // against and the country whose currency the amounts are in.
    const handleSelectDoctor = (doc) => {
        if (!doc?.doctorId) return;
        handleCloseModal();
        setPickedDoctor(doc);
        setDoctorPickerOpen(false);
        setDoctorSearch('');
        setCurrentPage(1);
        dispatch(getDoctorById(doc.doctorId));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'specialtyId') {
            setFormData((prev) => ({ ...prev, specialtyId: value, appointmentTypeId: '', actualPrice: '', customPrice: '' }));
            setPriceError('');
        } else if (name === 'customPrice') {
            const intValue = value.replace(/[^0-9]/g, '');
            if (intValue === '' || (Number(intValue) >= 0 && Number(intValue) <= 100000)) {
                setFormData((prev) => ({ ...prev, [name]: intValue }));

                // Validate if custom price is different from actual price
                const actualPriceValue = parseFloat(formData.actualPrice);
                const customPriceValue = parseFloat(value);

                if (value !== '' && formData.actualPrice !== '' && customPriceValue === actualPriceValue) {
                    setPriceError(t('customPrice.same_price_error'));
                } else {
                    setPriceError('');
                }
            }
        } else if (name === 'actualPrice') {
            if (value === '' || (Number(value) >= 0 && Number(value) <= 100000)) {
                setFormData((prev) => ({ ...prev, [name]: value }));
            }
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const specialtyId = formData.specialtyId;
        const appointmentTypeId = formData.appointmentTypeId;
        const actualPriceValue = parseFloat(formData.actualPrice);
        const customPriceValue = parseFloat(formData.customPrice);

        // Validate that custom price is different from actual price
        if (customPriceValue === actualPriceValue) {
            setPriceError(t('customPrice.same_price_error'));
            return;
        }

        if (customPriceValue > 100000 || customPriceValue < 0 || actualPriceValue > 100000 || actualPriceValue < 0) {
            toast.warn(t('customPrice.max_price_error'));
            return;
        }

        const selectedSpecialityObj = appointmentSpeciality.find(
            (item) => item.specialtyId === specialtyId
        );
        const selectedAppt = selectedSpecialityObj?.appointmentTypes?.find(
            (appt) => appt.typeId === appointmentTypeId
        );

        Swal.fire({
            title: isEditing
                ? t('customPrice.update_confirmation_title')
                : t('customPrice.add_confirmation_title'),
            text: isEditing
                ? t('customPrice.update_confirmation_text')
                : t('customPrice.add_confirmation_text'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#ef4444',
            confirmButtonText: isEditing
                ? t('customPrice.update')
                : t('customPrice.save'),
            cancelButtonText: t('customPrice.cancel'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    if (isEditing) {
                        // Update existing custom price
                        const updatePayload = {
                            actualPrice: actualPriceValue,
                            customPrice: customPriceValue,
                            specialityName: selectedSpecialityObj?.name || editingItem.specialityName,
                            appointmentTypeName: selectedAppt?.type || editingItem.appointmentTypeName,
                        };

                        await dispatch(updateCustomPrice({
                            uniqueId: editingItem.uniqueId,
                            payload: updatePayload
                        })).unwrap();

                        toast.success(t('customPrice.update_success_text'));
                    } else {
                        // Create new custom price
                        const createPayload = {
                            doctorId: doctorId || selectedPatient?.doctorId || '',
                            patientId: patientId,
                            specialityId: specialtyId,
                            specialityName: selectedSpecialityObj?.name || t('customPrice.unknown'),
                            appointmentTypeId: appointmentTypeId,
                            appointmentTypeName: selectedAppt?.type || t('customPrice.unknown'),
                            actualPrice: actualPriceValue,
                            customPrice: customPriceValue,
                            status: 'active',
                        };

                        await dispatch(createCustomPrice(createPayload)).unwrap();

                        toast.success(t('customPrice.add_success_text'));
                    }

                    // Refresh the list
                    if (doctorId) {
                        dispatch(getCustomPricesByPatientAndDoctor({
                            patientId,
                            doctorId,
                            page: currentPage,
                            limit: 5
                        }));
                    } else {
                        dispatch(getCustomPricesByPatientId({
                            patientId,
                            page: currentPage,
                            limit: 5
                        }));
                    }

                    handleCloseModal();
                } catch (err) {
                    const errorMessage = extractErrorMessage(err, t('customPrice.error_text'), lang);
                    toast.warn(errorMessage);
                }
            }
        });
    };

    const handleDelete = async (uniqueId) => {
        Swal.fire({
            title: t('customPrice.delete_confirmation_title'),
            text: t('customPrice.delete_confirmation_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#4ABDBD',
            confirmButtonText: t('customPrice.delete'),
            cancelButtonText: t('customPrice.cancel'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await dispatch(deleteCustomPrice(uniqueId)).unwrap();

                    // Refresh the list
                    if (doctorId) {
                        dispatch(getCustomPricesByPatientAndDoctor({
                            patientId,
                            doctorId,
                            page: currentPage,
                            limit: 5
                        }));
                    } else {
                        dispatch(getCustomPricesByPatientId({
                            patientId,
                            page: currentPage,
                            limit: 5
                        }));
                    }

                    toast.success(t('customPrice.delete_success_text'));
                } catch (err) {
                    const errorMessage = extractErrorMessage(err, t('customPrice.delete_error_text'), lang);
                    toast.warn(errorMessage);
                }
            }
        });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setCurrentPage(newPage);
        }
    };

    const selectedSpecialityObj = appointmentSpeciality.find(
        (item) => item.specialtyId === formData.specialtyId
    );

    return (
        <div data-testid="custom-price-container" className="mt-4">
            <div data-testid="custom-price-header" className="flex flex-col sm:flex-row flex-wrap gap-4 justify-between items-center mb-6">
                <h2 data-testid="custom-price-heading" className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('customPrice.heading')}
                </h2>
                {canEdit && (
                    <button
                        data-testid="custom-price-add-button"
                        id="patient-profile-additional-input-add-price"
                        onClick={handleAddClick}
                        className="group flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer w-full sm:w-auto justify-center"
                    >
                        <Plus className="w-5 h-5" />
                        <span className="relative z-10">{t('customPrice.add_price')}</span>
                    </button>
                )}
            </div>

            {/* An admin prices ON BEHALF OF a doctor, so they name the doctor first. */}
            {isAdmin && (
                <div
                    data-testid="custom-price-doctor-picker"
                    ref={doctorPickerRef}
                    className="relative mb-6"
                >
                    <label
                        className="block text-sm font-semibold text-gray-700 mb-2"
                        htmlFor="custom-price-doctor-picker-button"
                        data-testid="custom-price-doctor-picker-label"
                    >
                        {t('customPrice.doctor_picker_label')}
                    </label>
                    <button
                        type="button"
                        id="custom-price-doctor-picker-button"
                        data-testid="custom-price-doctor-picker-button"
                        onClick={() => setDoctorPickerOpen((open) => !open)}
                        className="w-full sm:max-w-md flex items-center justify-between gap-2 p-3 rounded-xl border border-gray-300 bg-white text-left text-sm text-gray-700 hover:border-[#4ABDBD] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] transition-all duration-300 cursor-pointer"
                    >
                        <span className="flex items-center gap-2 min-w-0">
                            <Stethoscope className="w-4 h-4 text-[#4ABDBD] flex-shrink-0" />
                            <span className="truncate" data-testid="custom-price-doctor-picker-value">
                                {pickedDoctor ? pickedDoctor.name : t('customPrice.select_doctor')}
                            </span>
                        </span>
                        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </button>
                    <p
                        data-testid="custom-price-doctor-picker-hint"
                        className="mt-2 text-xs text-gray-500"
                    >
                        {pickedDoctor ? t('customPrice.doctor_picked_hint') : t('customPrice.doctor_picker_hint')}
                    </p>

                    {doctorPickerOpen && (
                        <div
                            data-testid="custom-price-doctor-dropdown"
                            className="absolute z-40 mt-2 w-full sm:max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
                        >
                            <div className="p-3 border-b border-gray-100">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        data-testid="custom-price-doctor-search"
                                        value={doctorSearch}
                                        onChange={(e) => setDoctorSearch(e.target.value)}
                                        maxLength={50}
                                        placeholder={t('customPrice.search_doctor')}
                                        className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                    />
                                </div>
                            </div>

                            {loadingList ? (
                                <div data-testid="custom-price-doctor-dropdown-loading" className="p-4 flex justify-center">
                                    <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                </div>
                            ) : (
                                <ul data-testid="custom-price-doctor-list" className="max-h-60 overflow-y-auto custom-scrollbar">
                                    {(doctorNameList || []).length === 0 ? (
                                        <li data-testid="custom-price-doctor-empty" className="px-4 py-3 text-sm text-gray-500 text-center">
                                            {t('customPrice.no_doctors_found')}
                                        </li>
                                    ) : (
                                        (doctorNameList || []).map((doc) => (
                                            <li
                                                key={doc.doctorId}
                                                data-testid={`custom-price-doctor-option-${doc.doctorId}`}
                                                onClick={() => handleSelectDoctor(doc)}
                                                className="px-4 py-2.5 text-sm text-gray-700 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-[#4ABDBD] hover:text-white transition"
                                            >
                                                {doc.name}
                                            </li>
                                        ))
                                    )}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}

            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    id="custom-price-modal"
                    data-testid="modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h3
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="custom-price-modal-title"
                                data-testid="modal-title"
                            >
                                {isEditing ? t('customPrice.edit_custom_price') : t('customPrice.add_custom_price')}
                            </h3>
                            <button
                                id="custom-price-modal-close-button"
                                onClick={handleCloseModal}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                data-testid="modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <form id="custom-price-form" onSubmit={handleSubmit} data-testid="custom-price-form">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="custom-price-specialty-input"
                                            id="custom-price-specialty-label"
                                            data-testid="specialty-label"
                                        >
                                            {t('customPrice.specialty_name')} <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            id="custom-price-specialty-input"
                                            name="specialtyId"
                                            value={formData.specialtyId}
                                            onChange={handleInputChange}
                                            className="tt-select w-full p-3 rounded-xl border border-gray-300 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300"
                                            required
                                            disabled={isEditing}
                                            data-testid="specialty-input"
                                        >
                                            <option value="">{t('customPrice.select_specialty')}</option>
                                            {appointmentSpeciality.map((spec) => (
                                                <SpecialtyOption key={spec.specialtyId} spec={spec} />
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="custom-price-appointment-type-input"
                                            id="custom-price-appointment-type-label"
                                            data-testid="appointment-type-label"
                                        >
                                            {t('customPrice.appointment_type')} <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            id="custom-price-appointment-type-input"
                                            name="appointmentTypeId"
                                            value={formData.appointmentTypeId}
                                            onChange={handleInputChange}
                                            className="tt-select w-full p-3 rounded-xl border border-gray-300 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300"
                                            required
                                            disabled={!formData.specialtyId || isEditing}
                                            data-testid="appointment-type-input"
                                        >
                                            <option value="">{t('customPrice.select_type')}</option>
                                            {selectedSpecialityObj?.appointmentTypes?.map((appt) => (
                                                <option key={appt.typeId} value={appt.typeId}>
                                                    {appt.type}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="custom-price-actual-price-input"
                                            id="custom-price-actual-price-label"
                                            data-testid="actual-price-label"
                                        >
                                            {t('customPrice.actual_price_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="custom-price-actual-price-input"
                                            type="number"
                                            name="actualPrice"
                                            value={formData.actualPrice}
                                            onChange={handleInputChange}
                                            className="w-full p-3 rounded-xl border border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed text-sm transition-all duration-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            required
                                            min="0"
                                            max="100000"
                                            step="0.01"
                                            disabled
                                            readOnly
                                            data-testid="actual-price-input"
                                        />
                                    </div>
                                    <div className="mb-4">
                                        <label
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                            htmlFor="custom-price-price-input"
                                            id="custom-price-price-label"
                                            data-testid="price-label"
                                        >
                                            {t('customPrice.price_label')} <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            id="custom-price-price-input"
                                            type="number"
                                            name="customPrice"
                                            value={formData.customPrice}
                                            onChange={handleInputChange}
                                            onKeyDown={(e) => { if (e.key === '.' || e.key === ',') e.preventDefault(); }}
                                            className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 text-sm transition-all duration-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${priceError
                                                ? 'border-red-500 focus:ring-red-500 hover:border-red-500'
                                                : 'border-gray-300 focus:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                                }`}
                                            required
                                            min="0"
                                            max="100000"
                                            step="1"
                                            data-testid="price-input"
                                        />
                                        {priceError && (
                                            <p className="mt-2 text-sm text-red-600 font-medium">
                                                {priceError}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="custom-price-form-cancel-button"
                                onClick={handleCloseModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={loading}
                                data-testid="form-cancel-button"
                            >
                                {t('customPrice.cancel')}
                            </button>
                            <button
                                type="submit"
                                id="custom-price-form-submit-button"
                                form="custom-price-form"
                                className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                disabled={loading || !!priceError}
                                data-testid="form-submit-button"
                            >
                                {loading && (
                                    <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {loading
                                    ? t('customPrice.loading')
                                    : isEditing
                                        ? t('customPrice.update')
                                        : t('customPrice.save')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading && customPrices.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[200px] px-4 sm:px-6 lg:px-8">
                    <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin"></div>
                    <p className="text-gray-500 text-center text-sm sm:text-base md:text-lg mt-4">
                        {t('customPrice.loading')}
                    </p>
                </div>
            ) : customPrices.length === 0 ? (
                <div className="flex flex-col items-center justify-center col-span-full min-h-[200px] px-4 sm:px-6 lg:px-8">
                    <img
                        src={emptykey}
                        alt={t('customPrice.no_custom_price_found')}
                        className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-56 lg:h-56 object-contain"
                    />
                    <p className="text-gray-500 text-center text-sm sm:text-base md:text-lg">
                        {t('customPrice.empty')}
                    </p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {customPrices.map((price, index) => (
                            <div
                                key={price.uniqueId || index}
                                className="group relative rounded-2xl border border-gray-200 bg-white shadow-md hover:shadow-xl hover:shadow-[#4ABDBD]/20 transition-all duration-300 overflow-hidden hover:-translate-y-1"
                                data-testid={`custom-price-card-${index}`}
                            >
                                {/* Header with Doctor Name */}
                                <div className="bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] p-3 sm:p-4">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                                                <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                                            </div>
                                            <h3 className="text-sm sm:text-base font-bold text-white truncate">
                                                {price.doctorName || '-'}
                                            </h3>
                                        </div>

                                        {/* Action Buttons — only with a doctor in hand, and
                                            the rows on screen are then that doctor's own. */}
                                        {canEdit && (
                                            <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                                                <button
                                                    data-testid={`custom-price-edit-button-${price.uniqueId || index}`}
                                                    id={`patient-profile-additional-input-edit-${index}`}
                                                    onClick={() => handleEditClick(price)}
                                                    className="group/btn p-1.5 sm:p-2 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all duration-200 cursor-pointer"
                                                    title={t('customPrice.edit')}
                                                >
                                                    <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                                                </button>
                                                <button
                                                    data-testid={`custom-price-delete-button-${price.uniqueId || index}`}
                                                    id={`patient-profile-additional-input-delete-${index}`}
                                                    onClick={() => handleDelete(price.uniqueId)}
                                                    className="group/btn p-1.5 sm:p-2 rounded-lg bg-white/20 hover:bg-red-500/80 backdrop-blur-sm transition-all duration-200 cursor-pointer"
                                                    title={t('customPrice.delete_title')}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-3 sm:p-4 space-y-2.5 sm:space-y-3">
                                    {/* Specialty Name and Appointment Type - Responsive Row/Column */}
                                    <div className="flex flex-col md:flex-row md:gap-4 gap-2.5">
                                        {/* Specialty Name */}
                                        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                                            <div className="mt-0.5 flex-shrink-0">
                                                <Stethoscope className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#4ABDBD]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium">
                                                    {t("customPrice.specialty_name")}
                                                </p>
                                                <p className="text-sm font-semibold text-gray-800 truncate">
                                                    <PriceCardSpecialtyName specialityId={price.specialityId} fallback={price.specialityName || getSpecialtyName(price.specialityId)} />
                                                </p>
                                            </div>
                                        </div>

                                        {/* Appointment Type */}
                                        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                                            <div className="mt-0.5 flex-shrink-0">
                                                <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#4ABDBD]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-gray-500 font-medium">
                                                    {t("customPrice.type_label")}
                                                </p>
                                                <p className="text-sm font-semibold text-gray-800 truncate">
                                                    {price.appointmentTypeName ||
                                                        getAppointmentTypeName(price.appointmentTypeId, price.specialityId)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-gray-200 my-2.5 sm:my-3"></div>

                                    {/* Prices Section */}
                                    <div className="space-y-2">
                                        {/* Actual Price */}
                                        <div className="flex items-center justify-between p-2 sm:p-2.5 bg-gray-50 rounded-lg">
                                            <span className="text-xs sm:text-sm text-gray-600 font-medium">
                                                {t("customPrice.actual_price_label")}
                                            </span>
                                            <span className="text-sm sm:text-base font-bold text-gray-500 line-through">
                                                {formatPrice(price.actualPrice)}
                                            </span>
                                        </div>

                                        {/* Custom Price */}
                                        <div className={`flex items-center justify-between p-2.5 sm:p-3 rounded-lg ${price.value === 'increase'
                                            ? 'bg-green-50 border border-green-200'
                                            : 'bg-red-50 border border-red-200'
                                            }`}>
                                            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                                                {price.value === 'increase' ? (
                                                    <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                                                ) : (
                                                    <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                                                )}
                                                <span className={`text-xs sm:text-sm font-semibold truncate ${price.value === 'increase' ? 'text-green-700' : 'text-red-700'
                                                    }`}>
                                                    {t("customPrice.price_display_label")}
                                                </span>
                                            </div>
                                            <span className={`text-base sm:text-lg font-bold flex-shrink-0 ml-2 ${price.value === 'increase' ? 'text-green-700' : 'text-red-700'
                                                }`}>
                                                {formatPrice(price.customPrice)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom accent */}
                                <div className={`h-1 ${price.value === 'increase'
                                    ? 'bg-gradient-to-r from-green-400 to-green-600'
                                    : 'bg-gradient-to-r from-red-400 to-red-600'
                                    }`}></div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div data-testid="custom-price-pagination" className="flex items-center justify-center gap-2 mt-6">
                            <button
                                data-testid="custom-price-prev-page-button"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || loading}
                                className="p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4ABDBD] hover:to-[#3AA8A8] text-gray-700 hover:text-white shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-gray-100 disabled:hover:to-gray-200 disabled:hover:text-gray-700"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>

                            <div className="flex items-center gap-2">
                                <span data-testid="custom-price-page-indicator" className="text-sm font-semibold text-gray-700">
                                    {t('customPrice.page')} {currentPage} {t('customPrice.of')} {pagination.totalPages}
                                </span>
                            </div>

                            <button
                                data-testid="custom-price-next-page-button"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === pagination.totalPages || loading}
                                className="p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4ABDBD] hover:to-[#3AA8A8] text-gray-700 hover:text-white shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-gray-100 disabled:hover:to-gray-200 disabled:hover:text-gray-700"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CustomPrice;
